<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useUserStore } from '../stores/userStore'
import SettingsSection from './global-settings/SettingsSection.vue'
import GlobalInfoSection from './global-settings/GlobalInfoSection.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'update:show'])

const userStore = useUserStore()

// Active section in the sidebar
const activeSection = ref('general')

// Settings menu groups
const settingsGroups = [
  {
    id: 'settings',
    label: 'Settings',
    icon: 'fas fa-cog',
    items: [
      { id: 'general', label: 'General', icon: 'fas fa-sliders-h' },
      { id: 'appearance', label: 'Appearance', icon: 'fas fa-palette' },
      { id: 'notifications', label: 'Notifications', icon: 'fas fa-bell' },
      { id: 'security', label: 'Security', icon: 'fas fa-shield-alt' },
      { id: 'team', label: 'Team', icon: 'fas fa-users' },
      { id: 'integrations', label: 'Integrations', icon: 'fas fa-plug' }
    ]
  },
  {
    id: 'global-info',
    label: 'Global Information',
    icon: 'fas fa-info-circle',
    items: [
      { id: 'system', label: 'System', icon: 'fas fa-server' },
      { id: 'about', label: 'About', icon: 'fas fa-info' }
    ]
  }
]

// Add boxscores-management for admins
if (userStore.currentUser?.role === 'admin') {
  settingsGroups[0].items.push({
    id: 'boxscores-management',
    label: 'Boxscores Management',
    icon: 'fas fa-database'
  })
}

// Computed property to get all menu items
const allMenuItems = computed(() => {
  return settingsGroups.flatMap(group => group.items)
})

// Close handler
const closePanel = () => {
  emit('update:show', false)
  emit('close')
}

// Handle escape key
const handleEscape = (event) => {
  if (event.key === 'Escape' && props.show) {
    closePanel()
  }
}

// Watch for escape key
onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="settings-panel">
      <div 
        v-if="show"
        class="global-settings-panel fixed inset-0 z-[10000] flex items-center justify-center p-4"
        @click.self="closePanel"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/30 backdrop-blur-sm" @click="closePanel"></div>
        
        <!-- Settings Panel Container -->
        <div class="relative w-full h-full max-w-[98vw] max-h-[98vh] bg-gray-50 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <i class="fas fa-cog text-pink-600"></i>
              </div>
              <div>
                <h1 class="text-2xl font-bold text-gray-900">Global Settings</h1>
                <p class="text-sm text-gray-500">Configure your application preferences</p>
              </div>
            </div>
            <button
              @click="closePanel"
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close (ESC)"
            >
              <i class="fas fa-times text-gray-500 text-xl"></i>
            </button>
          </div>

          <!-- Main Content Area -->
          <div class="flex flex-1 overflow-hidden">
            <!-- Left Sidebar -->
            <div class="w-64 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0">
              <nav class="p-4 space-y-6">
                <div v-for="group in settingsGroups" :key="group.id" class="space-y-2">
                  <!-- Group Header -->
                  <div class="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {{ group.label }}
                  </div>
                  
                  <!-- Group Items -->
                  <div class="space-y-1">
                    <button
                      v-for="item in group.items"
                      :key="item.id"
                      @click="activeSection = item.id"
                      class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                      :class="
                        activeSection === item.id
                          ? 'bg-pink-50 text-pink-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      "
                    >
                      <i :class="[item.icon, 'w-5 text-center']"></i>
                      <span>{{ item.label }}</span>
                    </button>
                  </div>
                </div>
              </nav>
            </div>

            <!-- Right Content Area -->
            <div class="flex-1 overflow-y-auto bg-gray-50">
              <div class="max-w-5xl mx-auto p-8">
                <!-- Settings Section -->
                <SettingsSection
                  v-if="['general', 'appearance', 'notifications', 'security', 'team', 'integrations', 'boxscores-management'].includes(activeSection)"
                  :active-tab="activeSection"
                />

                <!-- Global Info Section -->
                <GlobalInfoSection
                  v-else-if="['system', 'about'].includes(activeSection)"
                  :active-tab="activeSection"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.global-settings-panel {
  animation: fadeIn 0.2s ease-out;
}

.settings-panel-enter-active,
.settings-panel-leave-active {
  transition: opacity 0.2s ease;
}

.settings-panel-enter-from,
.settings-panel-leave-to {
  opacity: 0;
}

.settings-panel-enter-active > div:last-child,
.settings-panel-leave-active > div:last-child {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.settings-panel-enter-from > div:last-child,
.settings-panel-leave-to > div:last-child {
  opacity: 0;
  transform: scale(0.95);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
